import { Link } from "react-router-dom";
import { ArrowRight, Music, Headphones, Disc3, Cross } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet-async";
import { FeaturedArtist } from "@/components/sections/FeaturedArtist";
import { FeaturedRelease } from "@/components/sections/FeaturedRelease";
import { FeaturedArtistsGrid } from "@/components/sections/FeaturedArtistsGrid";
import AnimatedSection from "@/components/ui/animated-section";
import { ForgedLine } from "@/components/ui/forged-line";
import { WaveformCross } from "@/components/ui/waveform-cross";
import { CornerBrackets } from "@/components/ui/corner-brackets";
import logoDark from "@/assets/logo-dark.png";

const services = [
  { icon: Music, title: "Artist Development", description: "Comprehensive development programs for emerging artists across every genre." },
  { icon: Headphones, title: "Professional Mastering", description: "Industry-standard mastering to bring your sound to its fullest potential." },
  { icon: Disc3, title: "Release Management", description: "End-to-end release coordination from recording to distribution." },
  { icon: Cross, title: "Theological Integrity", description: "Grounded in Scripture. Every artist, every release, every decision." },
];

const steps = [
  { number: "01", title: "Apply", description: "Submit your application with your music, vision, and ministry statement." },
  { number: "02", title: "Develop", description: "Work with our team on mastering, branding, and release strategy." },
  { number: "03", title: "Release", description: "Launch your music to all major platforms with full label support." },
];

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Nail Point Records",
  "url": "https://nailpointrecords.com",
  "description": "A creative house for Christian artists who take their craft and their calling seriously.",
  "foundingDate": "2024",
  "sameAs": [],
};

const Index = () => {
  return (
    <Layout>
      <Helmet>
        <title>Nail Point Records | Christian Artist Development & Releases</title>
        <meta name="description" content="A creative house for Christian artists who take their craft and their calling seriously." />
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      </Helmet>

      {/* Hero — Cinematic, left-aligned */}
      <section className="relative bg-background text-foreground py-[100px] md:py-[120px] overflow-hidden">
        {/* Radial vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 30% 50%, hsl(358 72% 45% / 0.06) 0%, transparent 60%)",
          }}
        />
        {/* Brand symbol background element */}
        <div className="absolute inset-0 pointer-events-none select-none overflow-hidden flex items-center justify-end">
          <img
            src={logoDark}
            alt=""
            aria-hidden="true"
            className="w-[50vw] max-w-[700px] opacity-[0.03] blur-[20px] translate-x-[15%]"
          />
        </div>
        {/* Oversized background text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span className="text-[20vw] font-heading font-bold uppercase text-foreground/[0.03] leading-none tracking-tight">
            CREED
          </span>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-[1320px] mx-auto flex gap-8">
            {/* Vertical copper bar */}
            <div className="hidden md:flex pt-2">
              <ForgedLine variant="vertical" width="80px" />
            </div>

            <div className="flex-1">
              <AnimatedSection animation="fade-up" as="div" duration={600}>
                <CornerBrackets>
                  <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-[7rem] font-heading font-bold uppercase leading-[0.9] tracking-tight animate-heading-track">
                    Building Artists<br />Who Carry Weight.
                  </h1>
                </CornerBrackets>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" as="div" delay={100} duration={500}>
                <p className="text-lg md:text-xl text-muted-foreground mt-6 max-w-[600px] font-light">
                  A Christian creative house shaping sound with conviction and craft.
                </p>
                <ForgedLine variant="horizontal" className="mt-6" />
              </AnimatedSection>

              <AnimatedSection animation="fade-up" as="div" delay={250} duration={400}>
                <div className="flex flex-col sm:flex-row gap-4 mt-10">
                  <Button size="xl" asChild>
                    <Link to="/apply">Apply Now <ArrowRight className="ml-2 h-5 w-5" /></Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/artists">View Artists</Link>
                  </Button>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      <WaveformCross className="py-6" />

      {/* What We Do — Charcoal surface */}
      <section className="relative py-20 md:py-28 bg-card overflow-hidden">
        {/* Soft vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 70% 50%, hsl(358 72% 45% / 0.04) 0%, transparent 55%)",
          }}
        />
        {/* Oversized background text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span className="text-[18vw] font-heading font-bold uppercase text-foreground/[0.03] leading-none tracking-tight">
            CONVICTION
          </span>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection animation="fade-up" as="div">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground uppercase tracking-[0.08em] mb-2">
              What We Do
            </h2>
            <ForgedLine variant="horizontal" className="mb-4" />
            <p className="text-muted-foreground text-lg max-w-2xl mb-16">
              A full infrastructure for Christian artists — from development to distribution.
            </p>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 gap-x-12 gap-y-10 max-w-4xl">
            {services.map((service, i) => (
              <AnimatedSection key={service.title} animation="fade-up" as="div" delay={i * 50}>
                <div className="flex gap-4 items-start group hover-lift">
                  <div className="w-10 h-10 rounded-[4px] bg-accent/10 flex items-center justify-center shrink-0 group-hover:bg-accent/20 transition-colors duration-300">
                    <service.icon className="h-5 w-5 text-accent" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">{service.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{service.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <WaveformCross className="py-6" />

      {/* How We Work */}
      <section className="relative py-20 md:py-28 bg-background overflow-hidden">
        {/* Oversized background text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span className="text-[18vw] font-heading font-bold uppercase text-foreground/[0.03] leading-none tracking-tight">
            DISCIPLINE
          </span>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection animation="fade-up" as="div">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground uppercase tracking-[0.08em] mb-2">
              How We Work
            </h2>
            <ForgedLine variant="horizontal" className="mb-4" />
            <p className="text-muted-foreground text-lg max-w-2xl mb-16">
              From application to release day — a simple, artist-first process.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl">
            {steps.map((step, i) => (
              <AnimatedSection key={step.number} animation="fade-up" as="div" delay={i * 100}>
                <div className="relative">
                  <span className="text-5xl font-heading font-bold text-accent/80 block mb-3">
                    {step.number}
                  </span>
                  {/* Connecting line */}
                  {i < steps.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-full w-full h-[1px] bg-accent/20" />
                  )}
                  <h3 className="text-xl font-heading font-bold text-foreground mb-2">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">{step.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <WaveformCross className="py-6" />

      <FeaturedArtistsGrid />

      <WaveformCross className="py-6" />

      <FeaturedArtist />

      <WaveformCross className="py-6" />

      <FeaturedRelease />

      <WaveformCross className="py-6" />

      {/* CTA — Premium */}
      <section className="relative py-20 md:py-28 bg-background overflow-hidden">
        {/* Oversized background text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span className="text-[18vw] font-heading font-bold uppercase text-foreground/[0.03] leading-none tracking-tight">
            CRAFT
          </span>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto flex gap-6">
            <div className="hidden md:flex pt-1">
              <ForgedLine variant="vertical" width="60px" />
            </div>
            <div>
              <AnimatedSection animation="fade-up" as="div">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground uppercase tracking-[0.05em] leading-tight mb-4">
                  Built on Conviction.<br />Refined Through Craft.
                </h2>
                <p className="text-muted-foreground text-lg mb-8 max-w-xl">
                  We're looking for artists who are serious about their sound.
                </p>
                <Button size="xl" asChild>
                  <Link to="/apply">Start Your Application</Link>
                </Button>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
